import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { extractDriveFileId } from '@/features/templates/extract-drive-file-id';
import {
  type SeedEntry,
  fetchSeeds,
  importFromSeed,
  listTemplates,
  registerTemplate,
} from '@/features/templates/templates-api';
import { ApiError } from '@/lib/api-client';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import {
  ArrowUpRight,
  FileSpreadsheet,
  FileText,
  Loader2,
  Plus,
  Share2,
  Sparkles,
} from 'lucide-react';
import { type ReactElement, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

export const Route = createFileRoute('/app/templates/')({
  component: TemplatesPage,
});

const SKELETON_KEYS = ['s1', 's2', 's3', 's4'] as const;

function TemplatesPage() {
  const { t } = useTranslation();
  const templates = useQuery({ queryKey: ['templates'], queryFn: listTemplates });
  const seeds = useQuery({ queryKey: ['seeds'], queryFn: fetchSeeds });
  const list = templates.data?.templates ?? [];

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('app.layout.section.workspace', { defaultValue: 'Workspace' })}
        title={t('templates.list.title')}
        description={t('templates.list.description')}
        actions={
          <Badge variant="secondary" className="rounded-full px-3 py-1">
            {t('templates.list.totalLabel', {
              defaultValue: '{{count}} template',
              defaultValue_other: '{{count}} templates',
              count: list.length,
            })}
          </Badge>
        }
      />

      <section className="grid gap-4 md:grid-cols-2">
        <RegisterCard />
        <SeedCatalogCard seeds={seeds.data?.templates ?? []} loading={seeds.isLoading} />
      </section>

      <section className="space-y-4">
        <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('templates.list.registeredHeader')}
        </h2>
        {templates.isLoading ? (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {SKELETON_KEYS.map((k) => (
              <Skeleton key={k} className="h-[88px] rounded-xl" />
            ))}
          </div>
        ) : list.length === 0 ? (
          <EmptyState
            icon={FileSpreadsheet}
            title={t('templates.list.empty')}
            description={t('templates.list.emptyBody', {
              defaultValue: 'Register a Google Doc or Sheet, or import one from the seed catalog.',
            })}
          />
        ) : (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {list.map((tpl) => {
              const Icon = tpl.kind === 'gdoc' ? FileText : FileSpreadsheet;
              return (
                <div
                  key={tpl.id}
                  className="group relative flex items-start gap-3 overflow-hidden rounded-xl border border-border/70 bg-card p-4 shadow-soft transition-all hover:-translate-y-0.5 hover:border-primary/40 hover:shadow-elevate"
                >
                  <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-transform group-hover:scale-105">
                    <Icon className="size-5" aria-hidden />
                  </span>
                  <div className="min-w-0 flex-1 space-y-1">
                    <div className="flex items-center gap-2">
                      <Link
                        to="/app/templates/$id"
                        params={{ id: tpl.id }}
                        className="block min-w-0 flex-1 truncate text-sm font-semibold tracking-tight outline-none after:absolute after:inset-0 focus-visible:after:rounded-xl focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:ring-offset-2"
                      >
                        {tpl.name}
                      </Link>
                      <Badge variant="outline" className="shrink-0 px-1.5 text-[10px] uppercase">
                        {tpl.kind}
                      </Badge>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {tpl.category ?? t('templates.list.withoutCategory')}
                      {' · '}
                      {t('templates.list.placeholdersCount', { count: tpl.placeholders.length })}
                    </p>
                  </div>
                  <Link
                    to="/app/templates/$id/mappings"
                    params={{ id: tpl.id }}
                    className="relative z-10 inline-flex shrink-0 items-center gap-1 rounded-md border border-border/60 bg-background/60 px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:border-primary/40 hover:text-primary"
                  >
                    <Share2 className="size-3.5" aria-hidden />
                    {t('templates.list.mappingsLink')}
                    <ArrowUpRight className="size-3" aria-hidden />
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

interface RegisterValues {
  googleFileId: string;
  name?: string;
  category?: string;
}

function RegisterCard(): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const schema = useMemo(
    () =>
      z.object({
        googleFileId: z.string().min(8, t('templates.register.validation.fileIdTooShort')),
        name: z.string().max(200).optional().or(z.literal('')),
        category: z.string().max(120).optional().or(z.literal('')),
      }),
    [t],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<RegisterValues>({ resolver: zodResolver(schema) });

  async function onSubmit(values: RegisterValues): Promise<void> {
    setError(null);
    const fileId = extractDriveFileId(values.googleFileId);
    if (!fileId) {
      setError(t('templates.register.validation.fileIdInvalid'));
      return;
    }
    try {
      await registerTemplate({
        googleFileId: fileId,
        name: values.name?.trim() || undefined,
        category: values.category?.trim() || undefined,
      });
      toast.success(t('templates.register.success'));
      reset();
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    } catch (err) {
      const message = err instanceof ApiError ? err.message : t('templates.register.failed');
      setError(message);
    }
  }

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="inline-flex size-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Plus className="size-4" aria-hidden />
          </span>
          <div className="space-y-1">
            <CardTitle className="text-base">{t('templates.register.title')}</CardTitle>
            <CardDescription>{t('templates.register.description')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit(onSubmit)}>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="googleFileId">{t('templates.register.fileIdLabel')}</Label>
            <Input
              id="googleFileId"
              autoComplete="off"
              placeholder={t('templates.register.fileIdPlaceholder')}
              {...register('googleFileId')}
            />
            {errors.googleFileId ? (
              <p className="text-xs text-destructive">{errors.googleFileId.message}</p>
            ) : (
              <p className="text-xs text-muted-foreground">{t('templates.register.fileIdHelp')}</p>
            )}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tpl-name">{t('templates.register.nameLabel')}</Label>
              <Input id="tpl-name" {...register('name')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="tpl-category">{t('templates.register.categoryLabel')}</Label>
              <Input id="tpl-category" {...register('category')} />
            </div>
          </div>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Plus className="size-4" aria-hidden />
            )}
            {t('templates.register.submit')}
          </Button>
        </CardContent>
      </form>
    </Card>
  );
}

function SeedCatalogCard({
  seeds,
  loading,
}: {
  seeds: SeedEntry[];
  loading: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [pendingId, setPendingId] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (seedId: string) => importFromSeed(seedId),
    onSuccess: () => {
      toast.success(t('templates.seeds.importSuccess'));
      queryClient.invalidateQueries({ queryKey: ['templates'] });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : t('templates.seeds.importFailed');
      toast.error(message);
    },
    onSettled: () => setPendingId(null),
  });

  return (
    <Card className="border-border/70 shadow-soft">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="inline-flex size-9 items-center justify-center rounded-lg bg-accent/30 text-foreground">
            <Sparkles className="size-4" aria-hidden />
          </span>
          <div className="space-y-1">
            <CardTitle className="text-base">{t('templates.seeds.title')}</CardTitle>
            <CardDescription>{t('templates.seeds.description')}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 rounded-lg" />
            <Skeleton className="h-12 rounded-lg" />
          </div>
        ) : seeds.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            <Trans i18nKey="templates.seeds.empty" components={{ code: <code /> }} />
          </p>
        ) : (
          <ul className="space-y-2">
            {seeds.map((seed) => (
              <li
                key={seed.id}
                className="flex items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/40 p-3 text-sm transition-colors hover:border-primary/30"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">{seed.name}</div>
                  <div className="truncate text-xs text-muted-foreground">
                    {seed.category} · {seed.kind} ·{' '}
                    {t('templates.seeds.placeholdersCount', { count: seed.placeholders.length })}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={mutation.isPending && pendingId === seed.id}
                  onClick={() => {
                    setPendingId(seed.id);
                    mutation.mutate(seed.id);
                  }}
                >
                  {mutation.isPending && pendingId === seed.id ? (
                    <Loader2 className="size-3.5 animate-spin" aria-hidden />
                  ) : null}
                  {t('templates.seeds.importButton')}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
